import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import SuperadminLayout, { DarkCard } from "./SuperadminLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle, XCircle, FilePdf } from "@phosphor-icons/react";

export default function SuperadminModeration() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [reviewItem, setReviewItem] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/superadmin/moderation/suggestions", { params: { status } });
      setItems(data.items);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [status]);

  const review = async (newStatus, note) => {
    try {
      await api.patch(`/superadmin/moderation/suggestions/${reviewItem.suggestion_id}`, { status: newStatus, note });
      toast.success(newStatus === "accepted" ? "Suggestion acceptée" : "Suggestion rejetée");
      setReviewItem(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
  };

  return (
    <SuperadminLayout title="Modération" actions={
      <Select value={status} onValueChange={setStatus}>
        <SelectTrigger className="bg-[#081221] border-white/10 text-white w-48" data-testid="mod-status"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="pending">En attente</SelectItem>
          <SelectItem value="accepted">Acceptées</SelectItem>
          <SelectItem value="rejected">Rejetées</SelectItem>
        </SelectContent>
      </Select>
    }>
      <p className="text-sm text-[#8494A8] mb-4">Suggestions de nouveaux questionnaires proposées par les praticiens.</p>

      {loading && <p className="text-[#8494A8]">Chargement…</p>}
      {!loading && items.length === 0 && <DarkCard className="p-12 text-center"><p className="text-[#8494A8]">Aucune suggestion {status}</p></DarkCard>}

      <div className="space-y-4">
        {items.map((s) => (
          <DarkCard key={s.suggestion_id} className="p-6" data-testid={`suggestion-${s.suggestion_id}`}>
            <div className="flex justify-between items-start mb-3">
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-semibold text-lg">{s.title}</h3>
                <p className="text-xs text-[#8494A8] mt-1">
                  Par <strong>{s.user_name || s.user_email}</strong> · {new Date(s.created_at).toLocaleDateString("fr-FR")}
                </p>
              </div>
              <Badge className={
                s.status === "accepted" ? "bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/20" :
                s.status === "rejected" ? "bg-red-500/20 text-red-300 hover:bg-red-500/20" :
                "bg-amber-500/20 text-amber-300 hover:bg-amber-500/20"
              }>{s.status}</Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-xs uppercase tracking-widest text-[#8494A8] mb-1">Référence</p>
                <p className="text-sm text-[#B7C4D6]">{s.reference}</p>
              </div>
              {s.pdf && (
                <div>
                  <p className="text-xs uppercase tracking-widest text-[#8494A8] mb-1">Pièce jointe</p>
                  <a href={`/api/questionnaire-suggestions/${s.suggestion_id}/pdf`} target="_blank" rel="noreferrer"
                    className="text-[#7BC3D4] hover:text-white text-sm inline-flex items-center gap-1">
                    <FilePdf size={16} /> {s.pdf.original_filename}
                  </a>
                </div>
              )}
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-[#8494A8] mb-1">Description</p>
              <p className="text-sm text-[#B7C4D6] whitespace-pre-wrap">{s.description}</p>
            </div>
            {s.review_note && (
              <div className="mt-4 p-3 bg-white/[0.03] rounded-lg border border-white/5">
                <p className="text-xs uppercase tracking-widest text-[#8494A8] mb-1">Note de review</p>
                <p className="text-sm text-[#B7C4D6]">{s.review_note}</p>
              </div>
            )}
            {status === "pending" && (
              <div className="flex gap-2 mt-4 pt-4 border-t border-white/5">
                <Button size="sm" onClick={() => setReviewItem({ ...s, targetStatus: "accepted" })}
                  className="bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 border border-emerald-500/30" data-testid={`mod-accept-${s.suggestion_id}`}>
                  <CheckCircle size={14} className="mr-1" /> Accepter
                </Button>
                <Button size="sm" onClick={() => setReviewItem({ ...s, targetStatus: "rejected" })}
                  className="bg-red-500/20 text-red-300 hover:bg-red-500/30 border border-red-500/30" data-testid={`mod-reject-${s.suggestion_id}`}>
                  <XCircle size={14} className="mr-1" /> Rejeter
                </Button>
              </div>
            )}
          </DarkCard>
        ))}
      </div>

      {reviewItem && (
        <ReviewDialog item={reviewItem} onConfirm={(note) => review(reviewItem.targetStatus, note)} onClose={() => setReviewItem(null)} />
      )}
    </SuperadminLayout>
  );
}

function ReviewDialog({ item, onConfirm, onClose }) {
  const [note, setNote] = useState("");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0F2136] border-white/10 text-white">
        <DialogHeader><DialogTitle>{item.targetStatus === "accepted" ? "Accepter" : "Rejeter"} : {item.title}</DialogTitle></DialogHeader>
        <div className="py-2">
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4}
            placeholder="Note de modération (optionnelle, visible dans l'audit)"
            className="bg-[#081221] border-white/10 text-white placeholder:text-[#5F758F]" data-testid="review-note" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-[#B7C4D6] hover:bg-white/10 hover:text-white">Annuler</Button>
          <Button onClick={() => onConfirm(note)} className="bg-[#E4572E] hover:bg-[#F27551]" data-testid="review-confirm">Confirmer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
